import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import i18n from '@/i18n'
import { SUPPORTED_LANGUAGES } from '@/i18n'

const I18N_STORAGE_KEY = 'i18nextLng'

function getLanguageToPreserve() {
  const raw =
    i18n.language ||
    (typeof localStorage !== 'undefined' && localStorage.getItem(I18N_STORAGE_KEY)) ||
    'en'
  const lng = (typeof raw === 'string' ? raw : 'en').split('-')[0]
  return SUPPORTED_LANGUAGES.includes(lng) ? lng : 'en'
}

function restoreLanguagePreference(lng) {
  try {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(I18N_STORAGE_KEY, lng)
    }
    i18n.changeLanguage(lng)
  } catch {
    // ignore
  }
}

const AuthContext = createContext(null)

export const AuthProvider = ({ children, onLogout }) => {
  const [user, setUser] = useState(() => {
    try {
      const savedUser = localStorage.getItem('user')
      return savedUser ? JSON.parse(savedUser) : null
    } catch {
      return null
    }
  })
  const [loading, setLoading] = useState(true)
  const queryClient = useQueryClient()

  useEffect(() => {
    queueMicrotask(() => setLoading(false))
  }, [])

  const login = useCallback(userData => {
    setUser(userData)
    try {
      localStorage.setItem('user', JSON.stringify(userData))
    } catch (error) {
      console.warn('Failed to save user to localStorage:', error)
    }
  }, [])

  const logout = useCallback(() => {
    try {
      const languageToKeep = getLanguageToPreserve()
      onLogout?.()
      setUser(null)
      queryClient.clear()
      if (typeof sessionStorage !== 'undefined') {
        sessionStorage.clear()
      }
      localStorage.clear()
      restoreLanguagePreference(languageToKeep)
    } catch (error) {
      console.warn('Failed to clear state on logout:', error)
    }
  }, [queryClient, onLogout])

  const value = useMemo(
    () => ({
      user,
      loading,
      isAuthenticated: !!user,
      login,
      logout,
    }),
    [user, loading, login, logout]
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

// eslint-disable-next-line react-refresh/only-export-components
export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
